import { useCallback } from "react";
import {
  approveBudget,
  rejectBudget,
  updateBudget,
  IApprovalPayload,
} from "../../../../api/fetchBudget";
import {
  BudgetRecipientEnum,
  IBudgetFormData,
  IBudgetService,
  IBudgetSummary,
  IClientData,
  IVehicleData,
} from "../types";
import { buildBudgetApiPayload } from "../utils/buildBudgetApiPayload";
import { roundMoney } from "../utils/budgetCalculations";

interface IUseBudgetRoleActionsParams {
  budgetId: string;
  occurrenceId: string;
  initialData?: IBudgetFormData;
  rejectionObservation: string;
  files: Array<{
    file?: File;
    name: string;
    type?: string;
    size?: string | number;
  }>;
  recipient: string | undefined;
  clientData: IClientData | undefined;
  vehicleData: IVehicleData | undefined;
  services: IBudgetService[];
  profile?: BudgetRecipientEnum;
  budgetSummary?: IBudgetSummary;
  uploadFiles: (
    filesToUpload: File[],
    budgetIdToUse?: string
  ) => Promise<boolean>;
  buildFormDataToSend: () => IBudgetFormData;
  onSave?: (data: IBudgetFormData) => void;
}

export const useBudgetRoleActions = (params: IUseBudgetRoleActionsParams) => {
  const {
    budgetId,
    occurrenceId,
    initialData,
    rejectionObservation,
    files,
    recipient,
    clientData,
    vehicleData,
    services,
    profile,
    budgetSummary,
    uploadFiles,
    buildFormDataToSend,
    onSave,
  } = params;

  const getRole = (): "factory" | "consultant" => {
    return profile === BudgetRecipientEnum.TOWER ? "factory" : "consultant";
  };

  const buildApiPayload = useCallback(() => {
    return buildBudgetApiPayload({
      occurrenceId,
      recipient,
      clientData,
      vehicleData,
      services,
      proposalServices: initialData?.proposalServices,
      subscriptionBalanceToApply: budgetSummary?.subscriptionBalanceApplied ?? 0,
    });
  }, [
    occurrenceId,
    recipient,
    clientData,
    vehicleData,
    services,
    initialData?.proposalServices,
    budgetSummary?.subscriptionBalanceApplied,
  ]);

  const handleReject = useCallback(async () => {
    if (!initialData?.versionNumber || !budgetId) {
      alert("Erro: dados do orçamento não encontrados");
      return;
    }

    const apiPayload = buildApiPayload();
    const role = getRole();

    const result = await rejectBudget({
      budgetId,
      versionNumber: initialData.versionNumber,
      role,
      rejectionObservation: rejectionObservation || "",
      services: apiPayload.services,
      proposalServices: apiPayload.proposalServices,
      subtotal: apiPayload.subtotal,
      discount: apiPayload.discount,
      netTotal: apiPayload.netTotal,
      counterProposalObservation: initialData?.counterProposalObs,
      counterProposalValue: initialData?.counterProposalValue,
    });

    if (result.error) {
      alert(`Erro ao recusar orçamento: ${result.error}`);
      return;
    }

    const filesToUpload: File[] = files
      .filter((fileItem) => fileItem.file !== undefined)
      .map((fileItem) => fileItem.file as File);

    if (filesToUpload.length > 0) {
      await uploadFiles(filesToUpload, budgetId);
    }

    if (onSave) {
      const formDataToSend = buildFormDataToSend();
      onSave(formDataToSend);
    }
  }, [
    budgetId,
    initialData,
    rejectionObservation,
    files,
    buildApiPayload,
    uploadFiles,
    buildFormDataToSend,
    onSave,
  ]);

  const handleApprove = useCallback(async () => {
    if (!initialData?.versionNumber || !budgetId) {
      alert("Erro: dados do orçamento não encontrados");
      return;
    }

    const apiPayload = buildApiPayload();
    const role = getRole();

    const subtotal = budgetSummary?.subtotal ?? apiPayload.subtotal;
    const discount = budgetSummary?.discounts ?? apiPayload.discount;
    const subscriptionBalanceToApply = budgetSummary?.subscriptionBalanceApplied ?? apiPayload.subscriptionBalanceToApply ?? 0;

    const approvalPayload: IApprovalPayload = {
      services: apiPayload.services,
      proposalServices: apiPayload.proposalServices,
      subtotal,
      discount,
      subscriptionBalanceToApply,
      netTotal: roundMoney(subtotal - discount - subscriptionBalanceToApply),
      observation: rejectionObservation || initialData?.observation,
      counterProposalObservation: initialData?.counterProposalObs,
      counterProposalValue: initialData?.counterProposalValue,
    };

    const result = await approveBudget(
      budgetId,
      initialData.versionNumber,
      role,
      approvalPayload
    );

    if (result.error) {
      alert(`Erro ao aprovar orçamento: ${result.error}`);
      return;
    }

    // Upload de arquivos se houver
    const filesToUpload: File[] = files
      .filter((fileItem) => fileItem.file !== undefined)
      .map((fileItem) => fileItem.file as File);

    if (filesToUpload.length > 0) {
      await uploadFiles(filesToUpload, budgetId);
    }

    if (onSave) {
      const formDataToSend = buildFormDataToSend();
      onSave(formDataToSend);
    }
  }, [
    budgetId,
    initialData,
    rejectionObservation,
    budgetSummary?.subtotal,
    budgetSummary?.discounts,
    budgetSummary?.subscriptionBalanceApplied,
    files,
    buildApiPayload,
    uploadFiles,
    buildFormDataToSend,
    onSave,
  ]);

  const handleUpdate = useCallback(async () => {
    if (!initialData?.versionNumber || !budgetId) {
      alert("Erro: dados do orçamento não encontrados");
      return;
    }

    const apiPayload = buildApiPayload();
    const role = getRole();

    const subtotal = budgetSummary?.subtotal ?? apiPayload.subtotal;
    const discount = budgetSummary?.discounts ?? apiPayload.discount;
    const subscriptionBalanceToApply = budgetSummary?.subscriptionBalanceApplied ?? apiPayload.subscriptionBalanceToApply ?? 0;

    const updatePayload: IApprovalPayload = {
      services: apiPayload.services,
      proposalServices: apiPayload.proposalServices,
      subtotal,
      discount,
      subscriptionBalanceToApply,
      netTotal: roundMoney(subtotal - discount - subscriptionBalanceToApply),
      observation: initialData?.observation || apiPayload.observation || undefined,
      counterProposalObservation: initialData?.counterProposalObs,
      counterProposalValue: initialData?.counterProposalValue,
    };

    const result = await updateBudget(
      budgetId,
      initialData.versionNumber,
      role,
      updatePayload
    );

    if (result.error) {
      alert(`Erro ao atualizar orçamento: ${result.error}`);
      return;
    }

    // Upload de arquivos se houver
    const filesToUpload: File[] = files
      .filter((fileItem) => fileItem.file !== undefined)
      .map((fileItem) => fileItem.file as File);

    if (filesToUpload.length > 0) {
      await uploadFiles(filesToUpload, budgetId);
    }

    if (onSave) {
      const formDataToSend = buildFormDataToSend();
      onSave(formDataToSend);
    }
  }, [
    budgetId,
    initialData,
    budgetSummary?.subtotal,
    budgetSummary?.discounts,
    budgetSummary?.subscriptionBalanceApplied,
    files,
    buildApiPayload,
    uploadFiles,
    buildFormDataToSend,
    onSave,
  ]);

  return {
    handleReject,
    handleApprove,
    handleUpdate,
  };
};
